import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { showToast } from './Toast';
import { useAnalytics } from '../hooks/useAnalytics';
import { getIntroPricing } from '../utils/introOffer';
import { TrialTimeline, ReviewCard } from './TrialWall';
import CancelFlow from './CancelFlow';

const DEFAULT_INTRO_TERMS = {
  pro: {
    enabled: true,
    discount_percent: 45,
    discount_display: '45% off',
    regular_price_display: '$14.99/month',
    regular_annual_price_display: '$143.88/year',
  },
  family: {
    enabled: true,
    discount_percent: 45,
    discount_display: '45% off',
    regular_price_display: '$24.99/month',
    regular_annual_price_display: '$227.88/year',
  },
};

const SubscriptionModal = ({
  isOpen, onClose, apiClient, user,
  highlightTier = null,
  offer = null, // "winback" (from the win-back email): 33% off 3 monthly invoices
}) => {
  const { subscriptionStatus, waitForSubscriptionActivation } = useAuth();
  const { requireParentalVerification } = useProfile();
  const { trackCheckoutStarted } = useAnalytics();
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCancelFlow, setShowCancelFlow] = useState(false);
  // Same two-page flow as the TrialWall: non-members with a trial see the
  // timeline first, members land straight on the plans.
  const [screen, setScreen] = useState('timeline');
  const introOffer = subscriptionStatus?.introOffer;
  const hasIntroOffer = introOffer?.enabled !== false && introOffer?.eligible !== false;

  useEffect(() => {
    if (isOpen) {
      setScreen('timeline');
      if (hasIntroOffer || offer === 'winback') setBillingPeriod('monthly');
    }
  }, [isOpen, hasIntroOffer, offer]);

  if (!isOpen) return null;

  const getCurrentPlanKey = () => {
    const tier = subscriptionStatus?.subscriptionType;
    const tierMap = { 'NONE': 'free', 'STARTER': 'starter', 'PLUS': 'plus', 'PRO': 'plus', 'FAMILY': 'family', 'UNLIMITED': 'unlimited', 'PREMIUM': 'plus', 'EDDY': 'plus', 'PHILIP': 'family', 'NIKHIL': 'unlimited' };
    return tierMap[tier] || 'free';
  };
  const currentPlanKey = getCurrentPlanKey();
  const trialConfig = subscriptionStatus?.trialConfig;
  const hasTrial = (trialConfig?.duration_days || 0) > 0;
  const showTimeline = screen === 'timeline' && hasTrial && currentPlanKey === 'free';

  const planDescriptions = { free: 'Try Pixley', starter: 'For casual creators', plus: 'Best for most families', family: 'For multiple kids', unlimited: 'For frequent creators' };

  const plans = {
    free: { name: 'Free', monthlyPrice: 0, annualPrice: 0, starsPerWeek: 50, features: [{ text: 'Up to 1 min stories', on: true }, { text: '16:9 format only', on: true }, { text: 'English only', on: true }, { text: 'No downloads', on: false }], cta: 'Keep Free', disabled: true },
    starter: { name: 'Starter', monthlyPrice: 7.99, annualPrice: 5.99, starsPerWeek: 100, features: [{ text: 'Up to 1.5 min stories', on: true }, { text: '16:9 format only', on: true }, { text: 'English only', on: true }, { text: 'Watermarked downloads', on: true }], cta: 'Choose Starter' },
    plus: { name: 'Pro', monthlyPrice: 14.99, annualPrice: 11.99, starsPerWeek: 250, features: [{ text: 'Up to 3 min stories', on: true }, { text: '9:16 vertical + 16:9', on: true }, { text: 'All languages', on: true }, { text: 'Watermarked downloads', on: true }], cta: 'Start Pro', badge: 'MOST POPULAR' },
    family: { name: 'Family', monthlyPrice: 24.99, annualPrice: 18.99, starsPerWeek: 650, features: [{ text: 'Up to 3 min stories', on: true }, { text: 'All formats', on: true }, { text: 'All languages', on: true }, { text: 'Download videos', on: true }, { text: 'No watermark', on: true }], cta: 'Choose Family' },
    unlimited: { name: 'Max', monthlyPrice: 49.99, annualPrice: 37.99, starsPerWeek: 1500, features: [{ text: 'Up to 3 min stories', on: true }, { text: 'All formats', on: true }, { text: 'All languages', on: true }, { text: 'Download videos', on: true }, { text: 'No watermark', on: true }], cta: 'Choose Max', badge: 'BEST VALUE' },
  };

  const handleSubscribe = async (tier) => {
    const executeSubscribe = async () => {
      if (!user?.id || !user?.email) { showToast("Unable to process. Try logging out and back in.", "error"); return; }
      if (isProcessing) return;
      const hasActive = currentPlanKey !== 'free';

      if (hasActive && tier === 'free') {
        if (subscriptionStatus?.managementPlatform === 'app_store') {
          window.open('https://apps.apple.com/account/subscriptions', '_blank', 'noopener,noreferrer');
          return;
        }
        if (!window.confirm("Cancel your subscription? You'll keep access until end of billing period.")) return;
        try { setIsProcessing(true); const r = await apiClient.createStripePortalSession(user.id); if (r.status && r.data.portal_url) window.location.href = r.data.portal_url; else throw new Error(r.error); } catch (e) { showToast("Failed to open portal.", "error"); setIsProcessing(false); } return;
      }
      // Web-offer plans (RevenueCat Billing) are changed and cancelled on
      // their own billing page, not through our Stripe plan-change API.
      if (hasActive && subscriptionStatus?.managementPlatform === 'rc_billing') {
        const msg = tier === 'free'
          ? "We'll open your billing page, where you can cancel. You keep access until the end of your billing period."
          : "Your plan is billed through our web checkout. We'll open your billing page, where you can change or cancel your plan.";
        if (!window.confirm(msg)) return;
        try {
          setIsProcessing(true);
          const r = await apiClient.createStripePortalSession(user.id);
          if (r.status && r.data?.portal_url) window.location.href = r.data.portal_url;
          else throw new Error(r.error);
        } catch (e) { showToast(e?.message || "Couldn't open your billing page. Please try again.", "error"); setIsProcessing(false); }
        return;
      }
      if (hasActive && tier !== 'free') {
        const tierH = ['free','starter','plus','family','unlimited'];
        const isUp = tierH.indexOf(tier) > tierH.indexOf(currentPlanKey);
        if (!window.confirm(isUp ? "Upgrade? You'll be charged a prorated amount." : "Downgrade? Takes effect immediately.")) return;
        try {
          setIsProcessing(true);
          const r = await apiClient.updateStripeSubscription(user.id, tier, billingPeriod);
          if (r.status) {
            showToast(`${isUp ? 'Upgraded' : 'Downgraded'}! Updating your plan...`, "success");
            // Poll until RevenueCat reflects the tier change so the reload
            // lands on a fresh cache instead of the old tier (30-min TTL).
            await waitForSubscriptionActivation({
              untilChangedFrom: subscriptionStatus?.subscriptionType,
              timeoutMs: 20000,
              intervalMs: 2500,
            });
            window.location.reload();
          } else throw new Error(r.error);
        } catch (e) { showToast(e?.message || "Failed to update subscription.", "error"); setIsProcessing(false); } return;
      }
      try {
        setIsProcessing(true);
        trackCheckoutStarted('subscription', tier);
        const r = await apiClient.createStripeCheckoutSession(
          user.id,
          user.email,
          tier,
          billingPeriod,
          window.location.pathname + window.location.search,
          offer === 'winback' && billingPeriod === 'monthly' ? 'winback' : null,
        );
        if (r.status && r.data.checkout_url) window.location.href = r.data.checkout_url;
        else throw new Error(r.error);
      } catch (e) { showToast("Failed to start checkout.", "error"); setIsProcessing(false); }
    };
    const hasActive = currentPlanKey !== 'free';
    const action = hasActive && tier === 'free' ? 'cancel subscription' : !hasActive ? 'subscribe to premium' : 'change subscription';
    requireParentalVerification(action, executeSubscribe);
  };

  const renderPlanCard = (planKey, plan) => {
    const isCurrentPlan = planKey === currentPlanKey;
    const isHighlighted = plan.badge === 'MOST POPULAR';
    const price = billingPeriod === 'annual' ? plan.annualPrice : plan.monthlyPrice;
    const originalPrice = plan.monthlyPrice;
    const tierH = ['free','starter','plus','family','unlimited'];
    const isDown = tierH.indexOf(planKey) < tierH.indexOf(currentPlanKey);
    const isUp = tierH.indexOf(planKey) > tierH.indexOf(currentPlanKey);
    const hasActive = currentPlanKey !== 'free';
    const offerTier = planKey === 'plus' ? 'pro' : planKey;
    const introTerms = introOffer?.plans?.[offerTier] || DEFAULT_INTRO_TERMS[offerTier];
    // From the win-back email, monthly plans are priced with the win-back
    // coupon checkout applies (33% off each of the first 3 monthly invoices),
    // never the new-customer intro — the card must show what they'll pay.
    const winbackPrice = offer === 'winback' && billingPeriod === 'monthly' && !hasActive && planKey !== 'free';
    const showIntroPrice = winbackPrice || (
      !hasActive
      && hasIntroOffer
      && introTerms?.enabled === true
    );
    const introPricing = getIntroPricing({
      monthlyPrice: plan.monthlyPrice,
      annualMonthlyPrice: plan.annualPrice,
      billingPeriod,
      discountPercent: winbackPrice ? 33 : (introTerms?.discount_percent || 45),
    });
    const introPeriodLine = winbackPrice ? 'a month for your first 3 months' : `for your ${introPricing.periodLabel}`;

    let ctaText = plan.cta;
    let isDisabled = false;
    if (isCurrentPlan) { ctaText = ['starter','unlimited'].includes(planKey) ? 'Your plan (legacy)' : 'Current Plan'; isDisabled = true; }
    else if (hasActive && isDown) { ctaText = planKey === 'free' ? 'Cancel' : 'Downgrade'; }
    else if (hasActive && isUp) { ctaText = 'Upgrade'; }
    else if (showIntroPrice) { ctaText = `Start ${plan.name} — ${introPricing.discountedPrice}${winbackPrice ? '/mo' : ''}`; }

    return (
      <div key={planKey} style={{ position: "relative", display: "flex", flexDirection: "column" }}>
        {plan.badge && (
          <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", zIndex: 2 }}>
            <span style={{ padding: "5px 14px", borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#FFF", background: plan.badge === 'MOST POPULAR' ? "#0065F8" : "#FF6B9A", boxShadow: "0px 3px 0px " + (plan.badge === 'MOST POPULAR' ? "#073B8E" : "#C94D73"), whiteSpace: "nowrap" }}>{plan.badge}</span>
          </div>
        )}

        <div style={{
          flex: 1, borderRadius: 20, padding: "28px 20px 20px", display: "flex", flexDirection: "column",
          background: "#FFFFFF",
          border: isHighlighted ? "2px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
          boxShadow: isHighlighted ? "0px 4px 0px rgba(0,101,248,0.15), 0px 12px 32px rgba(0,101,248,0.10)" : "0px 2px 0px rgba(11,26,51,0.04)",
          fontFamily: "'Inter', 'Fredoka', sans-serif",
        }}>
          {/* Plan name */}
          <h3 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 22, fontWeight: 700, color: "#0065F8", textAlign: "center", margin: 0, textTransform: "uppercase" }}>{plan.name}</h3>
          <p style={{ fontSize: 12, color: "#334155", textAlign: "center", margin: "2px 0 12px" }}>{planDescriptions[planKey]}</p>

          {/* Price */}
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            {planKey === 'free' ? (
              <>
                <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 42, fontWeight: 700, color: "#0B1A33", lineHeight: 1 }}>$0</div>
                <div style={{ fontSize: 12, color: "transparent", marginTop: 4, userSelect: "none" }}>&nbsp;<br/>&nbsp;</div>
              </>
            ) : (
              <>
                {showIntroPrice ? (
                  <>
                    <div style={{ display: "inline-flex", alignItems: "center", padding: "7px 16px", borderRadius: 999, background: "#E8F2FF", color: "#0065F8", fontFamily: "'Fredoka', sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: "0.04em", marginBottom: 12 }}>
                      {introPricing.discountPercent}% OFF
                    </div>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: billingPeriod === 'annual' ? 12 : 16, flexWrap: "nowrap", whiteSpace: "nowrap", width: "100%" }}>
                      <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: billingPeriod === 'annual' ? 34 : 40, fontWeight: 700, color: "#8A99B3", lineHeight: 1, letterSpacing: "-0.02em", textDecoration: "line-through", textDecorationThickness: 3 }}>
                        {introPricing.regularPrice}
                      </span>
                      <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: billingPeriod === 'annual' ? 34 : 40, fontWeight: 700, color: "#0B1A33", lineHeight: 1, letterSpacing: "-0.02em" }}>
                        {introPricing.discountedPrice}
                      </span>
                    </div>
                    <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 15, fontWeight: 600, color: "#334155", marginTop: 5 }}>
                      {introPeriodLine}
                    </div>
                    <div style={{ fontSize: 12, color: "#5A6B85", marginTop: 5 }}>
                      Then {introPricing.renewalPrice} · cancel anytime
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 3 }}>
                      <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 42, fontWeight: 700, color: "#0B1A33", lineHeight: 1 }}>${price}</span>
                      <span style={{ fontSize: 14, color: "#334155", fontWeight: 500 }}>/mo</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#334155", marginTop: 4 }}>
                      {billingPeriod === 'annual' ? (
                    <><span style={{ textDecoration: "line-through" }}>${originalPrice} monthly</span><br/>billed annually</>
                      ) : 'Billed monthly'}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          {/* Stars */}
          <div style={{ padding: "10px 12px", borderRadius: 14, background: "#EAF3FF", border: "1px solid rgba(0,101,248,0.1)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 16 }}>
            <img src="/assets/pixley_stars.png" alt="Stars" style={{ width: 20, height: 20 }} onError={(e) => { e.target.style.display = 'none'; }} />
            <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 18, fontWeight: 700, color: "#0065F8" }}>{plan.starsPerWeek}</span>
            <span style={{ fontSize: 12, color: "#334155" }}>stars / <strong style={{ fontFamily: "'Fredoka', sans-serif", fontWeight: 700, color: "#0065F8" }}>week</strong></span>
          </div>

          {/* Features */}
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px", display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
            {plan.features.map((f, i) => (
              <li key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#0B1A33", fontWeight: 400 }}>
                <span style={{ width: 18, height: 18, borderRadius: 999, flexShrink: 0, background: f.on ? "#E7F0FF" : "#F0F4FA", color: f.on ? "#0065F8" : "#C3D0E2", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  {f.on ? (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12.5l5.5 5.5L20 6.5" /></svg>
                  ) : (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round"><path d="M5 12h14" /></svg>
                  )}
                </span>
                <span style={{ color: f.on ? "#0B1A33" : "#334155" }}>{f.text}</span>
              </li>
            ))}
          </ul>

          {/* CTA */}
          <button
            onClick={() => !isDisabled && handleSubscribe(planKey)}
            disabled={isDisabled || isProcessing}
            style={{
              width: "100%", padding: "12px 16px", borderRadius: 14, fontSize: 15, fontWeight: 700, cursor: isDisabled ? "not-allowed" : "pointer",
              fontFamily: "'Fredoka', sans-serif",
              background: isDisabled ? "#EAF3FF" : isHighlighted ? "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)" : "#FFFFFF",
              color: isDisabled ? "#8CA0BC" : isHighlighted ? "#FFFFFF" : "#0065F8",
              boxShadow: isDisabled ? "none" : isHighlighted ? "0px 4px 0px #073B8E" : "0px 3px 0px rgba(11,26,51,0.06)",
              border: isDisabled || isHighlighted ? "none" : "2px solid #0065F8",
              transition: "transform 120ms cubic-bezier(.2,.8,.2,1), box-shadow 120ms cubic-bezier(.2,.8,.2,1)",
            }}
            onMouseDown={(e) => { if (!isDisabled) { e.currentTarget.style.transform = "translateY(3px)"; e.currentTarget.style.boxShadow = "0px 0px 0px transparent"; } }}
            onMouseUp={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = isHighlighted ? "0px 4px 0px #073B8E" : "0px 3px 0px rgba(11,26,51,0.06)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = isHighlighted ? "0px 4px 0px #073B8E" : "0px 3px 0px rgba(11,26,51,0.06)"; }}
          >
            {isProcessing ? 'Processing...' : ctaText}
          </button>
        </div>
      </div>
    );
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-2 sm:p-4" style={{ background: "rgba(11,26,51,0.42)", backdropFilter: "blur(8px)" }}>
      <div onClick={onClose} className="absolute inset-0" />

      <div style={{
        position: "relative", width: "100%", maxWidth: showTimeline ? 560 : 1200, minHeight: "min(700px, 88vh)", maxHeight: "92vh", transition: "max-width 300ms ease",
        background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        borderRadius: 28,
        boxShadow: "0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)",
        overflow: "hidden", display: "flex", flexDirection: "column",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
      }}>
        {/* Decorative background shapes */}
        <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
          <div style={{ position: "absolute", bottom: -80, right: -80, width: 260, height: 260, borderRadius: "50%", background: "rgba(0,202,255,0.05)" }} />
          <div style={{ position: "absolute", top: "40%", left: "10%", width: 120, height: 120, borderRadius: "50%", background: "rgba(127,198,255,0.04)" }} />
        </div>

        {/* Back to the trial timeline */}
        {hasTrial && currentPlanKey === 'free' && !showTimeline && (
          <button onClick={() => setScreen('timeline')} aria-label="Back" style={{ position: "absolute", top: 16, left: 16, zIndex: 10, width: 44, height: 44, borderRadius: 14, background: "#FFFFFF", border: "2px solid rgba(11,26,51,0.08)", cursor: "pointer", boxShadow: "0px 3px 0px rgba(11,26,51,0.06)", display: "flex", alignItems: "center", justifyContent: "center", color: "#334155" }}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Close */}
        <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, zIndex: 10, width: 44, height: 44, borderRadius: 14, background: "#FFFFFF", border: "2px solid rgba(11,26,51,0.08)", cursor: "pointer", boxShadow: "0px 3px 0px rgba(11,26,51,0.06)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, color: "#334155" }}>
          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div style={{ position: "relative", padding: "24px 24px 20px", overflowY: "auto" }}>
          <style>{`
            @keyframes submodal-confetti-fall {
              0% { top: -6%; transform: rotate(0deg); opacity: 1; }
              80% { opacity: 1; }
              100% { top: 104%; transform: rotate(420deg); opacity: 0; }
            }
            @keyframes tw-step-in {
              0% { opacity: 0; transform: translateY(12px); }
              100% { opacity: 1; transform: translateY(0); }
            }
            @keyframes tw-chip-pop {
              0% { transform: scale(0.3); }
              70% { transform: scale(1.18); }
              100% { transform: scale(1); }
            }
            @keyframes tw-rail-grow {
              0% { transform: scaleY(0); }
              100% { transform: scaleY(1); }
            }
            @keyframes tw-screen-in {
              0% { opacity: 0; transform: translateX(36px); }
              100% { opacity: 1; transform: translateX(0); }
            }
          `}</style>
          {/* Header — Pip hero (matches the TrialWall) */}
          <div style={{ position: "relative", textAlign: "center", margin: "-24px -24px 20px", overflow: "hidden", background: "radial-gradient(120% 100% at 50% 0%, #DCEBFF 0%, rgba(220,235,255,0) 78%)", padding: "30px 24px 8px" }}>
            {["#0065F8", "#FF6B9A", "#FFC93C", "#37D67A", "#8A5CF6", "#00CAFF"].map((color, i) => (
              <span
                key={i}
                aria-hidden="true"
                style={{
                  position: "absolute", top: "-6%", left: `${(i * 31 + 9) % 100}%`,
                  width: 7 + (i % 3) * 3, height: 4,
                  background: color, borderRadius: 2,
                  transform: `rotate(${(i * 53) % 360}deg)`,
                  animation: `submodal-confetti-fall ${3.4 + (i % 4) * 0.5}s linear ${(i % 5) * 0.6}s infinite`,
                  pointerEvents: "none", opacity: 0.85,
                }}
              />
            ))}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 10, position: "relative" }}>
              <img src="/assets/pixley-logo-full.png" alt="Pixley" style={{ height: 96, objectFit: "contain" }} />
            </div>
            <h2 style={{ fontFamily: "'Fredoka', sans-serif", fontSize: "clamp(22px, 3vw, 30px)", fontWeight: 700, color: "#0B1A33", margin: "0 0 6px", lineHeight: 1.15, position: "relative" }}>
              Choose the right plan for your family
            </h2>
            <p style={{ fontSize: 14.5, color: "#5A6B85", margin: 0, position: "relative" }}>More stars, longer stories, and premium tools for every kind of creator.</p>
            {offer === 'winback' && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginTop: 14, padding: "8px 16px 8px 8px", borderRadius: 999, background: "#FFF6DC", border: "2px solid rgba(232,148,16,0.25)", position: "relative" }}>
                <img src="/assets/cancel/pip_heart.webp" alt="" style={{ width: 34, height: 34, objectFit: "contain" }} />
                <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 14.5, fontWeight: 700, color: "#0B1A33" }}>
                  Welcome back! 33% off your first 3 months on monthly plans.
                </span>
              </div>
            )}
          </div>

          {showTimeline ? (
            <div key="timeline" style={{ animation: "tw-screen-in 0.4s ease-out both" }}>
              <TrialTimeline trialConfig={trialConfig} />
              <div style={{ animation: "tw-step-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) 1.5s both", marginBottom: 18 }}>
                <ReviewCard />
              </div>
              <div style={{ width: "100%", maxWidth: 430, margin: "0 auto", animation: "tw-step-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) 1.7s both" }}>
                <button
                  onClick={() => setScreen('plans')}
                  style={{ fontFamily: "'Fredoka', sans-serif", width: "100%", padding: "14px 0", borderRadius: 14, fontSize: 16, fontWeight: 700, cursor: "pointer", color: "#FFFFFF", background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)", border: "1px solid rgba(255,255,255,0.5)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4), 0px 4px 0px #073B8E" }}
                >
                  Continue
                </button>
              </div>
            </div>
          ) : (
          <div key="plans" style={{ animation: "tw-screen-in 0.4s ease-out both" }}>
          {/* Billing toggle */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 28 }}>
            <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 14, fontWeight: 700, color: billingPeriod === 'monthly' ? "#0B1A33" : "#8CA0BC" }}>Monthly</span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
              style={{ position: "relative", width: 56, height: 30, borderRadius: 999, background: billingPeriod === 'annual' ? "#0065F8" : "#D7E3F3", border: "none", cursor: "pointer", transition: "background 200ms ease", boxShadow: "inset 0 2px 4px rgba(11,26,51,0.1)" }}
            >
              <div style={{ position: "absolute", top: 3, left: billingPeriod === 'annual' ? 29 : 3, width: 24, height: 24, borderRadius: "50%", background: "#FFF", boxShadow: "0px 2px 4px rgba(11,26,51,0.2)", transition: "left 200ms ease" }} />
            </button>
            <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 14, fontWeight: 700, color: billingPeriod === 'annual' ? "#0B1A33" : "#8CA0BC" }}>Annual</span>
            <span style={{ padding: "5px 12px", borderRadius: 999, fontSize: 11, fontWeight: 800, background: "#0065F8", color: "#FFF", boxShadow: "0px 2px 0px #073B8E", letterSpacing: "0.04em" }}>SAVE 25%</span>
          </div>

          {/* Plans grid — members-only model: Pro + Family are the sellable
              plans. The Free card is gone; Starter/Max render ONLY as the
              user's current (grandfathered) plan, never as a choice. */}
          {(() => {
            const sellable = ['plus', 'family'];
            const visible = ['starter', 'plus', 'family', 'unlimited'].filter(
              (k) => sellable.includes(k) || k === currentPlanKey,
            );
            return (
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${visible.length}, 1fr)`, gap: 14, marginBottom: 24, maxWidth: visible.length <= 2 ? 560 : undefined, marginLeft: "auto", marginRight: "auto" }} className="subscription-plans-grid">
                {visible.map((k) => renderPlanCard(k, plans[k]))}
              </div>
            );
          })()}

          {/* Cancel path (was the Free card's role) for active subscribers */}
          {currentPlanKey !== 'free' && ['stripe', 'rc_billing'].includes(subscriptionStatus?.managementPlatform) && (
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <button
                onClick={() => setShowCancelFlow(true)}
                style={{ background: "none", border: "none", color: "#8A99B3", fontSize: 12.5, textDecoration: "underline", cursor: "pointer" }}
              >
                Cancel subscription
              </button>
            </div>
          )}
          {currentPlanKey !== 'free' && subscriptionStatus?.managementPlatform === 'app_store' && (
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <a
                href="https://apps.apple.com/account/subscriptions"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#0065F8", fontSize: 12.5, fontWeight: 600 }}
              >
                Manage subscription in the App Store
              </a>
            </div>
          )}

          {/* Footer */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 24, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155" }}>
              <svg width="14" height="14" fill="none" stroke="#0065F8" viewBox="0 0 24 24" strokeWidth={2}><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
              <span>Secure checkout</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155" }}>
              <svg width="14" height="14" fill="none" stroke="#0065F8" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              <span>Restore purchases</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155" }}>
              <svg width="14" height="14" fill="none" stroke="#0065F8" viewBox="0 0 24 24" strokeWidth={2}><circle cx="12" cy="12" r="10" /><path strokeLinecap="round" d="M12 16v-4m0-4h.01" /></svg>
              <span>Subscriptions renew automatically unless canceled.</span>
            </div>
          </div>
          </div>
          )}
        </div>
      </div>

      <CancelFlow
        isOpen={showCancelFlow}
        onClose={() => setShowCancelFlow(false)}
        onChanged={() => { if (typeof onClose === 'function') onClose(); }}
      />

      <style>{`
        @media (max-width: 900px) {
          .subscription-plans-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 500px) {
          .subscription-plans-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default SubscriptionModal;
